import {Component, Input} from '@angular/core';

/**
 * navItem "Haftung" of the Provisionssatz OBC, widget alias `cp.contractAgent.liability`.
 *
 * The liability bookkeeping: which Haftungstyp was applied and where it came from
 * (`liabilityTypeAssignment` vs. the matrix overwrite, `appliedLiabilitySource`), the period
 * itself, the payment-free months that extend it, and the two amounts that decide the payout
 * brake — `inLiabilityAmount` and `outOfLiabilityAmount`.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * THIS PANE IS SPLIT DOWN THE MIDDLE — three deferred fields, seven that can never be empty
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * DEFERRED (run-owned, nullable, no default) — these carry "nicht berechnet" while empty:
 *
 *   liabilityTypeAssignment :404  @ManyToOne(optional = true)
 *   liabilityType           :428  @ManyToOne(optional = true)
 *   appliedLiabilitySource  :447  @Column
 *
 * CAPTURED OR ALWAYS PRESENT (`nullable = false`) — a fallback on these would be dead code:
 *
 *   liabilityMonths     :691  @Column(nullable = false)
 *   liabilityStartDate  :713  @Column(nullable = false, columnDefinition = "DATE")
 *   liabilityEndDate    :737  @Column(nullable = false, columnDefinition = "DATE")
 *   paymentFreeMonths   :760  @NotNull @Column(nullable = false), default 0
 *   inLiabilityAmount   :783  @Column(nullable = false, precision = 19, scale = 2)
 *   outOfLiabilityAmount:806  @NotNull @Column(nullable = false, precision = 19, scale = 2)
 *   liabilityStatus     :832  @NotNull @Enumerated(ORDINAL) @Column(nullable = false)
 *
 * HISTORY OF THIS SPLIT, so it is not undone by accident (HIGH-1). An earlier revision of this
 * class carried the opposite instruction — "the pending duty now lives entirely on the parent, do
 * not re-add it here". That sentence was written when the deferred set had just been reduced to
 * the NOT NULL check, and for the seven fields above it is still correct. It was WRONG as a
 * statement about the pane, because the three nullable fields in the same list fail that check and
 * do belong to the run. The duty is decided per FIELD, never per pane — re-apply the two
 * conditions (nullable without default AND written only by `ProvisionCalculationService`) before
 * moving any field between the two groups.
 */
@Component({
    selector: 'cp-provision-contract-agent-liability',
    templateUrl: './cp-provision-contract-agent-liability.component.html',
    styleUrls: ['./cp-provision-contract-agent-liability.component.scss'],
    standalone: false
})
export class CpProvisionContractAgentLiabilityComponent {

    @Input() provisionContractAgentId: number;

    /** Fields per row — the side view passes 1. */
    @Input() columns: number = 2;

    /** Visible fields, in the order of the requirement. */
    readonly fields: string[] = [
        'liabilityTypeDtoName',
        'liabilityTypeAssignmentDtoName',
        'appliedLiabilitySource',
        'liabilityMonths',
        'liabilityStartDate',
        'liabilityEndDate',
        'paymentFreeMonths',
        'inLiabilityAmount',
        'outOfLiabilityAmount',
        'liabilityStatus'
    ];

    /**
     * The three deferred fields of this pane — see the split in the class javadoc. The two FKs are
     * addressed by their `...DtoName` variant, because that is the field the widget renders.
     */
    readonly pendingFields: string[] = [
        'liabilityTypeDtoName',
        'liabilityTypeAssignmentDtoName',
        'appliedLiabilitySource'
    ];

}
